import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { API } from '../../App';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import { Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

// Auto-generate the same friendly default the backend uses, so the user
// sees the suggested name update live as they pick a date range.
const autoName = (start, end) => {
  if (!start || !end) return '';
  try {
    const s = new Date(start);
    const e = new Date(end);
    if (isNaN(s) || isNaN(e)) return '';
    const monthName = s.toLocaleString('en-GB', { month: 'long' });
    const days = Math.round((e - s) / 86400000) + 1;
    const first = new Date(s.getFullYear(), s.getMonth(), 1);
    const last = new Date(s.getFullYear(), s.getMonth() + 1, 0);
    if (s.getTime() === first.getTime() && e.getTime() === last.getTime()) return `${monthName} Full Month`;
    if (s.getMonth() === e.getMonth() && days <= 7) {
      const firstDow = (first.getDay() + 6) % 7; // 0 = Mon
      const offset = firstDow === 0 ? 0 : (7 - firstDow);
      const day = s.getDate();
      const weekNum = day <= offset ? 1 : Math.floor((day - offset - 1) / 7) + 2;
      return `${monthName} Week ${weekNum}`;
    }
    if (days >= 13 && days <= 15) return `${monthName} 2-Week`;
    return `${monthName} ${s.getDate()}-${e.getDate()}`;
  } catch { return ''; }
};

const CreateReportModal = ({ open, onClose }) => {
  const today = new Date().toISOString().slice(0, 10);
  const [start, setStart] = useState(today);
  const [end, setEnd] = useState(today);
  const [name, setName] = useState('');
  const [creating, setCreating] = useState(false);
  const navigate = useNavigate();

  useEffect(() => { setName(autoName(start, end)); }, [start, end]);

  const handleCreate = async () => {
    if (!start || !end) { toast.error('Pick a from + to date'); return; }
    if (end < start) { toast.error('End date must be on or after start date'); return; }
    setCreating(true);
    try {
      const gen = await api.post(`${API}/pi-counts/reports/generate`, { start_date: start, end_date: end });
      const saved = await api.post(`${API}/pi-counts/reports`, { ...gen.data, name: name || gen.data.name });
      toast.success(`Report created: ${saved.data.name}`);
      onClose?.();
      navigate(`/pi-counts/reports/${saved.data.id}`);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      toast.error(detail ? `Report failed: ${detail}${status ? ` (${status})` : ''}` : `Report failed: ${e?.message || 'unknown error'}`);
    } finally { setCreating(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-md rounded-2xl" data-testid="create-report-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-violet-700">
            <Sparkles className="w-5 h-5" />
            Create Report
          </DialogTitle>
          <DialogDescription>
            Pick a date range. We'll roll up every count + adjustment in that window
            into a editable report. The default name is generated for you.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="rep-start" className="text-xs">From</Label>
              <Input id="rep-start" type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="report-start-date" />
            </div>
            <div>
              <Label htmlFor="rep-end" className="text-xs">To</Label>
              <Input id="rep-end" type="date" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="report-end-date" />
            </div>
          </div>
          <div>
            <Label htmlFor="rep-name" className="text-xs">Report Name</Label>
            <Input id="rep-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. May Week 1" data-testid="report-name" />
            <p className="text-[10px] text-gray-500 mt-1">Auto-generated from the date range — edit if needed.</p>
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={handleCreate}
            disabled={creating}
            className="text-white"
            style={{ background: 'linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%)' }}
            data-testid="report-create-confirm"
          >
            {creating ? 'Creating…' : 'Create Report'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CreateReportModal;
